// vg-binding: P7.D-148
//
// apps/vendor/src/features/orders/OrdersTabs.tsx
//
// 6-tab status filter amendment. The 6 tabs map to the
// vendor-visible statuses; admin-only states (assigned, pending_reassignment)
// are intentionally not exposed.

import { cn } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import type { OrderStatus } from './api.js';

export type OrdersTabKey =
  | 'all'
  | 'accepted'
  | 'in_production'
  | 'quality_check'
  | 'ready_to_ship'
  | 'shipped'
  | 'completed';

interface Tab {
  key: OrdersTabKey;
  /** i18n key under `orders.tabs.*` */
  labelKey: string;
  status: OrderStatus | 'all';
  testId: string;
}

const TABS: readonly Tab[] = [
  { key: 'all', labelKey: 'tabs.all', status: 'all', testId: 'orders-tab-all' },
  { key: 'accepted', labelKey: 'tabs.accepted', status: 'accepted', testId: 'orders-tab-accepted' },
  { key: 'in_production', labelKey: 'tabs.inProduction', status: 'in_production', testId: 'orders-tab-in-production' },
  { key: 'quality_check', labelKey: 'tabs.qualityCheck', status: 'quality_check', testId: 'orders-tab-quality-check' },
  { key: 'ready_to_ship', labelKey: 'tabs.readyToShip', status: 'ready_to_ship', testId: 'orders-tab-ready-to-ship' },
  { key: 'shipped', labelKey: 'tabs.shipped', status: 'shipped', testId: 'orders-tab-shipped' },
  { key: 'completed', labelKey: 'tabs.completed', status: 'completed', testId: 'orders-tab-completed' },
];

interface Props {
  active: OrdersTabKey;
  onChange: (tab: OrdersTabKey) => void;
  /**
   * dbg-20260704 — per-status counts (keyed by VendorAssignment.status, plus
   * `all`). When provided, each tab shows an `(N)` badge and hides itself when
   * its count is 0 — only `all` and the active tab stay visible at 0, so the
   * strip only surfaces buckets that hold work. Omitted → all tabs shown
   * (no badges), preserving the pre-count behaviour.
   */
  counts?: Record<string, number>;
}

export function OrdersTabs({ active, onChange, counts }: Props): JSX.Element {
  const { t } = useTranslation('orders');

  const countFor = (tab: Tab): number | undefined => {
    if (!counts) return undefined;
    return tab.key === 'all' ? counts.all ?? 0 : counts[tab.status] ?? 0;
  };

  return (
    <nav
      aria-label={t('tabs.ariaLabel')}
      className="flex w-full items-center gap-1 overflow-x-auto border-b border-pw-border"
    >
      {TABS.filter((tab) => {
        // Always keep All + the active tab; hide any other whose count is 0.
        if (tab.key === 'all' || tab.key === active) return true;
        if (!counts) return true;
        return (countFor(tab) ?? 0) > 0;
      }).map((tab) => {
        const isActive = tab.key === active;
        const count = countFor(tab);
        return (
          <button
            key={tab.key}
            type="button"
            data-testid={tab.testId}
            onClick={() => onChange(tab.key)}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'border-pw-primary text-pw-primary-strong'
                : 'border-transparent text-pw-text-muted hover:text-pw-text',
            )}
          >
            <span>{t(tab.labelKey)}</span>
            {typeof count === 'number' ? (
              <span
                data-testid={`${tab.testId}-count`}
                className="tabular-nums text-xs opacity-80"
              >
                ({count})
              </span>
            ) : null}
          </button>
        );
      })}
    </nav>
  );
}

export function tabKeyToStatus(key: OrdersTabKey): OrderStatus | 'all' {
  const tab = TABS.find((t) => t.key === key);
  return tab?.status ?? 'all';
}

export default OrdersTabs;
